/**
 * Приложение управления заказами для администратора смены.
 *
 * URL: /employee/orders/{uid}
 * URL: /approval/{token} — страница согласования воронок (публичная)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { getOrderByUid, confirmOrder, cancelOrder } from './api/client';
import { OrderCard } from './components/OrderCard';
import { ActionButtons } from './components/ActionButtons';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { ErrorPage } from './components/ErrorPage';
import { Toast, toastStyles } from './components/Toast';
import { reachGoal } from './utils/metrika';
import ApprovalPage from './components/ApprovalPage';

// Проверяем, является ли текущий путь страницей согласования
function isApprovalPath() {
  return window.location.pathname.startsWith('/approval/');
}

// Получаем UID из URL
function getUidFromUrl() {
  const pathParts = window.location.pathname.split('/');
  // URL: /employee/orders/{uid}
  const ordersIndex = pathParts.indexOf('orders');
  if (ordersIndex !== -1 && pathParts[ordersIndex + 1]) {
    return pathParts[ordersIndex + 1];
  }
  return null;
}

function App() {
  // Если путь /approval/{token} — рендерим страницу согласования
  if (isApprovalPath()) {
    return <ApprovalPage />;
  }

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const isFirstLoad = useRef(true);

  const uid = getUidFromUrl();

  const fetchOrder = useCallback(async (silent = false) => {
    if (!uid) {
      setError('UID заказа не указан');
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    try {
      setError(null);
      const data = await getOrderByUid(uid);
      setOrder(data);
    } catch (err) {
      console.error('Error fetching order:', err);
      if (err.response?.status === 404) {
        setError('Заказ не найден');
      } else if (!silent) {
        setError(err.response?.data?.error || 'Не удалось загрузить заказ');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [uid]);

  useEffect(() => {
    fetchOrder();

    // Автообновление каждые 30 секунд для отслеживания статуса
    const interval = setInterval(() => fetchOrder(true), 30000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  const handleConfirm = async () => {
    try {
      setActionLoading(true);
      const updatedOrder = await confirmOrder(uid);
      setOrder(updatedOrder);
      
      reachGoal('change_order_status', {
        order_id: uid,
        new_status: 'confirmed',
        old_status: order?.status,
      });
      
      setToast({ message: 'Заказ подтверждён!', type: 'success' });
    } catch (err) {
      console.error('Error confirming order:', err);
      setToast({
        message: err.response?.data?.error || 'Не удалось подтвердить заказ',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (reason) => {
    try {
      setActionLoading(true);
      const updatedOrder = await cancelOrder(uid, reason, 'admin');
      setOrder(updatedOrder);

      reachGoal('change_order_status', {
        order_id: uid,
        new_status: 'cancelled',
        old_status: order?.status,
        has_refund: updatedOrder.refund?.success || false,
      });

      const hasRefund = updatedOrder.refund?.success && updatedOrder.order_type !== 'booking_no_prepayment';
      setToast({
        message: hasRefund
          ? 'Заказ отменён, средства возвращены покупателю'
          : 'Заказ отменён',
        type: 'success',
      });
    } catch (err) {
      console.error('Error cancelling order:', err);
      setToast({
        message: err.response?.data?.error || 'Не удалось отменить заказ',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return <ErrorPage message={error} onRetry={fetchOrder} />;
  }

  return (
    <>
      {/* Toast styles */}
      <style>{toastStyles}</style>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-6 px-4 pb-24">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Header */}
          <div className="text-center mb-2">
            <h1 className="text-lg font-semibold text-gray-500 dark:text-gray-400">
              Управление заказом
            </h1>
          </div>

          {/* Карточка заказа */}
          {order && <OrderCard order={order} />}
        </div>
      </div>

      {/* Закреплённые кнопки действий внизу экрана */}
      {order && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 p-4 z-40">
          <div className="max-w-lg mx-auto">
            <ActionButtons
              order={order}
              onConfirm={handleConfirm}
              onCancel={handleCancel}
              isLoading={actionLoading}
            />
          </div>
        </div>
      )}

      {/* Toast уведомление */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}

export default App;
